/**
 * Providers API — RTK Query endpoints for the verification-provider
 * catalogue and a user's own provider order per category (email/phone).
 *
 * Routes through Express at `/api/providers`, which proxies to
 * Infynd-CL-Workflow-Service's real `/api/v1/providers` endpoints.
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import type { Provider, ProviderCategory, SaveProviderSelectionRequest } from "@/lib/types/provider.types";

interface Envelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

export const providersApi = createApi({
  reducerPath: "providersApi",
  baseQuery: axiosBaseQuery("/api/providers"),
  tagTypes: ["Providers", "ProviderSelection"],
  endpoints: (builder) => ({
    // GET / — the provider catalogue, optionally filtered
    getProviders: builder.query<Provider[], { category?: ProviderCategory; active?: boolean } | void>({
      query: (params) => ({
        url: "/",
        params: params
          ? { category: params.category, active: params.active === undefined ? undefined : String(params.active) }
          : undefined,
      }),
      transformResponse: (response: Envelope<Provider[]>) => response.data,
      providesTags: ["Providers"],
    }),

    // GET /selection?category= — this user's saved order for one category
    getProviderSelection: builder.query<Provider[], ProviderCategory>({
      query: (category) => ({ url: "/selection", params: { category } }),
      transformResponse: (response: Envelope<Provider[]>) => response.data,
      providesTags: (result, error, category) => [{ type: "ProviderSelection" as const, id: category }],
    }),

    // PUT /selection — replace this user's whole selection for one category
    saveProviderSelection: builder.mutation<Provider[], SaveProviderSelectionRequest>({
      query: ({ category, providerIds }) => ({
        url: "/selection",
        method: "PUT",
        body: { category, providerIds },
      }),
      transformResponse: (response: Envelope<Provider[]>) => response.data,
      invalidatesTags: (result, error, { category }) => [{ type: "ProviderSelection" as const, id: category }],
    }),
  }),
});

export const {
  useGetProvidersQuery,
  useGetProviderSelectionQuery,
  useSaveProviderSelectionMutation,
} = providersApi;
